import { createWebHistory, createRouter } from "vue-router";

import routes from "./routes";
import PermissionService from "../helpers/permissionService";
import { logActivity } from "../helpers/logService";
import { clearClientSession } from "../helpers/sessionHelper";

const router = createRouter({
  history: createWebHistory(),
  routes,

  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    return { x: 0, y: 0 };
  },
});

/**
 * Ambil label menu dari route.
 * Lebih aman daripada routeTo.matched[0].meta.label
 * karena matched[0] belum tentu punya meta label.
 */
function getRouteMenuLabel(route) {
  return (
    route.meta?.label ||
    route.matched.find((item) => item.meta?.label)?.meta?.label ||
    ""
  );
}

/**
 * Parse localStorage yang kadang disimpan sebagai JSON array,
 * kadang sebagai string comma-separated.
 */
function getStorageArray(key) {
  const value = localStorage.getItem(key);

  if (!value) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);

    if (Array.isArray(parsed)) {
      return parsed;
    }

    return [parsed];
  } catch (error) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
}

/**
 * Before each route evaluates...
 */
router.beforeEach(async (routeTo, routeFrom, next) => {
  try {
    const authRequired = routeTo.matched.some(
      (route) => route.meta.authRequired,
    );
    const authPesertaRequired = routeTo.matched.some(
      (route) => route.meta.authPesertaRequired,
    );

    const requiresAuth = authRequired || authPesertaRequired;
    console.log({requiresAuth});

    if (!requiresAuth) {
      return next();
    }

    /**
     * Kalau route logout memang punya logic sendiri,
     * biarkan masuk ke halaman logout.
     */
    if (routeTo.name === "logout") {
      return next();
    }

    /**
     * Karena JWT sekarang ada di HttpOnly cookie,
     * frontend tidak bisa cek token via localStorage.
     *
     * Maka validasi login harus lewat backend.
     * PermissionService.cekToken() harus memanggil endpoint backend
     * dengan withCredentials: true.
     */
    const isAuthenticated = await PermissionService.cekToken();
    console.log({ isAuthenticated });

    if (!isAuthenticated) {
      clearClientSession();

      return next({
        path: "/login",
        query: {
          redirect: routeTo.fullPath,
        },
      });
    }

    /**
     * Kalau route hanya butuh auth peserta,
     * cukup sampai validasi authenticated saja.
     */
    if (authPesertaRequired) {
      return next();
    }

    /**
     * Menu Penyusun Soal, Validator, dan Kompetensi Validator hanya boleh
     * diakses oleh user yang BUKAN validator dan BUKAN penyusun soal.
     */
    const restrictedForValidatorOrPenyusun = routeTo.matched.some(
      (route) => route.meta.restrictedForValidatorOrPenyusun,
    );

    if (restrictedForValidatorOrPenyusun) {
      const isValidator = localStorage.getItem("is_validator") === "true";
      const isPenyusunSoal =
        localStorage.getItem("is_penyusunSoal") === "true";

      if (isValidator || isPenyusunSoal) {
        return next("/403");
      }
    }

    const isCheck = routeTo.matched.some((route) => route.meta.isCheck);
    const menuLabel = getRouteMenuLabel(routeTo);

    /**
     * Cek hak akses read/write menu berdasarkan menuRead & menuWrite
     * dari response role_cermat (disimpan saat login sebagai
     * menu_read_names / menu_write_names).
     */
    const isMenuWrite = menuLabel ? PermissionService.isWrite(menuLabel) : false;
    const isMenuRead = menuLabel ? PermissionService.isRead(menuLabel) : false;

    localStorage.setItem("isWrite", isMenuWrite ? "true" : "false");
    localStorage.setItem("isRead", isMenuRead ? "true" : "false");

    /**
     * Kalau menu butuh dicek dan user tidak punya hak read,
     * lebih aman arahkan ke 403.
     */
    if (isCheck && menuLabel && !isMenuRead) {
      return next("/403");
    }

    /**
     * Menu pegawai (login-pegawai) tidak memakai skema filtered_name_menu /
     * roles_data_akses_menu milik login admin/CERMAT, jadi pengecekannya
     * di-skip berdasarkan meta rute (skipMenuAccessCheck), bukan flag
     * localStorage — supaya tidak tergantung pada urutan login/logout
     * sebelumnya di browser yang sama.
     */
    const skipMenuAccessCheck = routeTo.matched.some(
      (route) => route.meta.skipMenuAccessCheck,
    );
    if (!skipMenuAccessCheck) {

      /**
       * Cek apakah menu termasuk dalam daftar menu yang boleh diakses.
       */
      const filteredNameMenu = getStorageArray("filtered_name_menu") || [];

      if (isCheck && menuLabel && !filteredNameMenu.includes(menuLabel)) {
        return next("/403");
      }

      /**
       * Set kode tes aktif berdasarkan roles_data_akses_menu.
       */
      const rolesDataAksesMenu = getStorageArray("roles_data_akses_menu")|| [];
      const rolesDataAksesKodeTes =
        localStorage.getItem("roles_data_akses_kode_tes") || "";

      if (menuLabel && rolesDataAksesMenu.includes(menuLabel)) {
        localStorage.setItem("kode_tes_active", rolesDataAksesKodeTes);
      } else {
        localStorage.setItem("kode_tes_active", "");
      }
    } else {
      localStorage.setItem("kode_tes_active", "");
    }

    return next();
  } catch (error) {
    console.log({ "error router beforeEach": error });
    clearClientSession();

    return next({
      path: "/login",
      query: {
        redirect: routeTo.fullPath,
      },
    });
  }
});

router.afterEach(async (to) => {
  try {
    const menuName = to.meta.label;
    const shouldLog =
      !to.meta.authPesertaRequired &&
      to.name !== "login" &&
      to.name !== "logout" &&
      to.name !== "403";

    const typeLog = to.meta.typeLog ?? "akses menu";

    if (menuName && shouldLog) {
      logActivity(menuName, typeLog);
    }
  } catch (error) {
    console.log({ "error afterEach": error });
  }
});

export default router;
